import type { components } from '../../generated/fct';
import { ApiError, ApiRequest } from '../shared';

export const USER_PREFERENCE_KEY = {
  COMPARISON_STATUS_DEFAULT: 'PREFERENCE.GENERAL.COMPARISON_STATUS_DEFAULT',
  FINAL_IMPRESSIONS_REVIEW_SCORE_THRESHOLD:
    'PREFERENCE.GENERAL.FINAL_IMPRESSIONS_REVIEW_SCORE_THRESHOLD',
  KNOWN_HX_CHECKBOX: 'PREFERENCE.GENERAL.KNOWN_HX_CHECKBOX',
  SHOW_REQUIRED_ACTIONS_UPFRONT: 'PREFERENCE.GENERAL.SHOW_REQUIRED_ACTIONS_UPFRONT',
} as const;

export type UserPreferenceKey = (typeof USER_PREFERENCE_KEY)[keyof typeof USER_PREFERENCE_KEY];

export type UserPreferenceValueType = components['schemas']['PreferenceValueTypeEnum'];

export const USER_PREFERENCE_VALUE_TYPE = {
  BOOLEAN: 'boolean',
  ENUM: 'enum',
  FLOAT: 'float',
  INTEGER: 'integer',
  JSON: 'json',
  STRING: 'string',
} as const satisfies Record<string, UserPreferenceValueType>;

export type UserPreferenceCategory = components['schemas']['PreferenceCategoryEnum'];

export const USER_PREFERENCE_CATEGORY = {
  GENERAL: 'general',
} as const satisfies Record<string, UserPreferenceCategory>;

export type UserPreferenceOption = components['schemas']['UserPreferenceOptionSchema'];

export interface UserPreferenceUiConfig {
  [key: string]: unknown;
  controlType?: string;
  helpText?: Record<string, string>;
  label?: string;
  sortOrder?: number;
}

type GeneratedUserPreference = components['schemas']['UserPreferenceResponseSchema'];

export type UserPreference = Omit<
  GeneratedUserPreference,
  'category' | 'key' | 'uiConfig' | 'valueType'
> & {
  category: null | UserPreferenceCategory;
  key: UserPreferenceKey;
  uiConfig: null | UserPreferenceUiConfig;
  valueType: UserPreferenceValueType;
};

export type UserPreferencesResponse = UserPreference[];

export type UserPreferencesError = ApiError<unknown>;

export type UserPreferenceUpdateItem = Omit<
  components['schemas']['UserPreferenceUpdateItem'],
  'key'
> & {
  key: UserPreferenceKey;
};

export type PatchUserPreferencesPayload = Omit<
  components['schemas']['UserPreferenceBulkUpdateSchema'],
  'preferences'
> & {
  preferences: UserPreferenceUpdateItem[];
};

export type PatchUserPreferencesRequest = ApiRequest<PatchUserPreferencesPayload>;

export type PatchUserPreferencesResponse = UserPreference[];

export type PatchUserPreferencesError = ApiError<unknown>;

export type InitUserPreferencesError = ApiError<unknown>;

export type DefaultUserPreferencesResponse = UserPreference[];

export type DefaultUserPreferencesError = ApiError<unknown>;
